import { ArrowLeft } from "lucide-react";
import { useContext, useEffect, useMemo, useRef } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import BlockRenderer from "../../components/content/BlockRenderer";
import Footer from "../../components/marketing/engineering/Footer";
import Reveal from "../../components/marketing/Reveal";
import Seo from "../../components/seo/Seo";
import { blogPosts } from "../../content/blogPosts";
import { ApplicationContext } from "../../context/GlobalState";
import { setPageMetadata } from "../../context/actions";
import MarketingLayout from "../../layouts/MarketingLayout";
import {
  formatBlogPostDate,
  getBlogPostBySlug,
  getPlainTextFromBlocks,
} from "../../utils/siteUtils";

import MetaText from "../../components/marketing/MetaText";

/**
 * Individual blog post route.
 * Translation: Individual blog post route.
 *
 * @returns {JSX.Element} Blog post page.
 */
const BlogPost = () => {
  const heroRef = useRef(null);
  const { slug } = useParams();
  const [, dispatch] = useContext(ApplicationContext);

  const post = useMemo(() => getBlogPostBySlug(blogPosts, slug), [slug]);

  useEffect(() => {
    if (!post) {
      return;
    }

    dispatch(
      setPageMetadata({
        title: `${post.title} | Data Chimp Labs`,
        description: post.metaDescription || getPlainTextFromBlocks(post.blocks),
        image: post.heroImage,
        pathname: `/blog/${post.slug}`,
      })
    );
  }, [dispatch, post]);

  if (!post) {
    return <Navigate to="/404" replace />;
  }

  return (
    <>
      <Seo
        title={`${post.title} | Data Chimp Labs`}
        description={post.metaDescription || getPlainTextFromBlocks(post.blocks)}
        pathname={`/blog/${post.slug}`}
        image={post.heroImage}
        articlePublishedAt={post.publishedAt}
      />

      <MarketingLayout heroRef={heroRef} footer={<Footer />}>
        <section ref={heroRef} className="blog-post">
          <div className="blog-post__inner">
            <Link
              to="/blog"
              className="blog-post__back-link"
              title="Return to the Data Chimp Labs blog index"
            >
              <ArrowLeft size={16} />
              <span>Back to Blog</span>
            </Link>

            <Reveal>
              <div className="blog-post__header">
                <MetaText className="blog-post__date">{formatBlogPostDate(post.publishedAt)}</MetaText>
                <h1>{post.title}</h1>
                <div className="glow-line" />
              </div>
            </Reveal>

            {post.heroImage ? (
              <Reveal delay={50}>
                <div className="blog-post__image-frame">
                  <img src={post.heroImage} alt={post.title} title={post.title} />
                </div>
              </Reveal>
            ) : null}

            <Reveal delay={100}>
              <article className="content">
                <BlockRenderer blocks={post.blocks} />
              </article>
            </Reveal>
          </div>
        </section>
      </MarketingLayout>
    </>
  );
};

export default BlogPost;
